import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from 'src/services/BaseService';
import Services from 'src/services/Services';
import { useLocalization } from 'src/context/LocalizationContext';

const MallManagementAddUserModal = ({
  dialogOpen,
  setDialogOpen,
  getUserList,
}) => {
  const { language } = useLocalization();
  const [loading, setLoading] = useState(false);
  const [availableMalls, setAvailableMalls] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    username: '',
  });

  const [selectedAuthorities, setSelectedAuthorities] = useState([]);
  const [hasFullAuthority, setHasFullAuthority] = useState(false);
  const [selectedMalls, setSelectedMalls] = useState([]);

  // Get current user's authorized malls
  useEffect(() => {
    if (dialogOpen) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const malls = user?.authLocations?.mall || [];
      setAvailableMalls(malls);
      // Set default authority to mall-management-user
      setSelectedAuthorities(['mall-management-user']);
      // If only one mall available, auto-select it
      if (malls.length === 1) {
        setSelectedMalls([malls[0].id]);
      }
    }
  }, [dialogOpen]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      setFormData({
        name: '',
        surname: '',
        email: '',
        phone: '',
        username: '',
      });
      setSelectedAuthorities([]);
      setSelectedMalls([]);
      setHasFullAuthority(false);
    }
  }, [dialogOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFullAuthorityChange = (e) => {
    const checked = e.target.checked;
    setHasFullAuthority(checked);
    // Update selectedAuthorities based on checkbox
    if (checked) {
      setSelectedAuthorities(['mall-management']);
    } else {
      setSelectedAuthorities(['mall-management-user']);
    }
  };

  const handleMallChange = (event) => {
    const value = event.target.value;
    setSelectedMalls(typeof value === 'string' ? value.split(',') : value);
  };

  // Generate 6-digit random password
  const generatePassword = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('İsim alanı zorunludur');
      return;
    }
    if (!formData.surname.trim()) {
      toast.error('Soyisim alanı zorunludur');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Telefon alanı zorunludur');
      return;
    }
    if (!formData.username.trim()) {
      toast.error('Kullanıcı adı alanı zorunludur');
      return;
    }
    if (selectedAuthorities.length === 0) {
      toast.error('En az bir yetki seçmelisiniz');
      return;
    }
    if (selectedMalls.length === 0) {
      toast.error('En az bir AVM seçmelisiniz');
      return;
    }

    setLoading(true);

    try {
      // Generate 6-digit password
      const generatedPassword = generatePassword();

      // Prepare allowedPoints if user has mall-management-user authority
      let allowedPoints = [];
      if (selectedAuthorities.includes('mall-management-user')) {
        // Get malkabul point ID from the first selected mall
        const firstMall = availableMalls.find((m) => m.id === selectedMalls[0]);
        const malkabulPointId = firstMall?.points?.malkabul?.id;

        if (malkabulPointId) {
          allowedPoints = [
            {
              id: malkabulPointId,
              isFirstResponsible: true,
            },
          ];
        }
      }

      const userData = {
        mallID: selectedMalls[0], // First selected mall
        allowedPoints: allowedPoints,
        email: formData.email,
        name: formData.name,
        type: 'user',
        lastname: formData.surname,
        phone: formData.phone,
        username: formData.username,
        password: generatedPassword,
        isAdministrator: false,
        accountCategory: 'mall-logistics',
        frontAuth: selectedAuthorities,
        authLocations: {
          mall: selectedMalls, // Just the IDs
        },
      };

      const response = await api.post('flow/users-create-employee', userData);

      if (response.data.status) {
        // Send SMS with password
        try {
          const smsMessage = `Merhaba ${formData.name}, AVM Yönetim Ekranları hesabınız oluşturuldu. Kullanıcı adı: ${formData.username}, Şifre: ${generatedPassword}`;
          await Services.SendCode(formData.phone, smsMessage);
          toast.success('Kullanıcı başarıyla oluşturuldu ve SMS gönderildi');
        } catch (smsError) {
          console.error('SMS gönderme hatası:', smsError);
          toast.warning('Kullanıcı oluşturuldu ancak SMS gönderilemedi');
        }

        setDialogOpen(false);
        if (getUserList) {
          getUserList();
        }
      } else {
        toast.error(response.data.message || 'Kullanıcı oluşturulurken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Kullanıcı oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDialogOpen(false);
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Yeni Kullanıcı Oluştur
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Kullanıcı Tipi: Kullanıcı
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
          {/* Name */}
          <TextField
            label="İsim"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
          />

          {/* Surname */}
          <TextField
            label="Soyisim"
            name="surname"
            value={formData.surname}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
          />

          {/* Email */}
          <TextField
            label="E-posta"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            disabled={loading}
          />

          {/* Phone */}
          <TextField
            label="Telefon"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
            placeholder="5XXXXXXXXX"
          />

          {/* Username */}
          <TextField
            label="Kullanıcı Adı"
            name="username"
            value={formData.username}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
          />

          {/* User Authorities */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Kullanıcı Yetkisi *
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasFullAuthority}
                  onChange={handleFullAuthorityChange}
                  disabled={loading}
                />
              }
              label="Tam Yetki"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
              {hasFullAuthority
                ? 'Kullanıcı tüm AVM yönetimi yetkilerine sahip olacak'
                : 'Kullanıcı sınırlı yetkilerle oluşturulacak'}
            </Typography>
          </Box>

          {/* Mall Selection */}
          <FormControl fullWidth required disabled={loading}>
            <InputLabel>Kullanıcı Konumu</InputLabel>
            <Select
              multiple
              value={selectedMalls}
              onChange={handleMallChange}
              label="Kullanıcı Konumu"
              renderValue={(selected) =>
                selected
                  .map((id) => availableMalls.find((m) => m.id === id)?.title)
                  .join(', ')
              }
            >
              {availableMalls.map((mall) => (
                <MenuItem key={mall.id} value={mall.id}>
                  <Checkbox checked={selectedMalls.indexOf(mall.id) > -1} />
                  {mall.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Oluşturuluyor...' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MallManagementAddUserModal;

